import React, { memo } from 'react';
import {
    TextField, Box, MenuItem, Autocomplete, IconButton, Button, Typography
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CustomDatePicker from '../CustomDatePicker';
import CurrencyInput from '../CurrencyInput';
import { COMMON_OPS_STATUS_OPTIONS } from '../../utils/bookingConstants';

// --- Component nút Bill dùng chung ---
const BillButton = ({ item, onClick }) => {
    if (!item) return null; 
    
    const hasBill = !!(item.bill_image || item.image || item.bill_url);
    
    return (
        <Button
            variant="outlined" 
            fullWidth         
            size="small"
            onClick={onClick}
            startIcon={hasBill ? <VisibilityIcon /> : <CloudUploadIcon />}
            sx={{ 
                height: '40px',        
                textTransform: 'none', 
                whiteSpace: 'nowrap',   
                justifyContent: 'center',
                
                borderColor: hasBill ? '#2e7d32' : 'rgba(0, 0, 0, 0.23)', 
                color: hasBill ? '#2e7d32' : 'rgba(0, 0, 0, 0.6)',
                fontWeight: hasBill ? 'bold' : 'normal',

                '&:hover': {
                    borderColor: hasBill ? '#1b5e20' : 'rgba(0, 0, 0, 0.87)',
                    backgroundColor: hasBill ? 'rgba(46, 125, 50, 0.04)' : 'rgba(0, 0, 0, 0.04)'
                }
            }}
        >
            {hasBill ? "Đã có Bill" : "Đăng Bill"}
        </Button>
    );
};

// --- 1. Style dùng chung ---
export const CustomLabel = ({ title, required = false }) => (
    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, color: '#2c3e50', fontSize: '0.9rem' }}>
        {title} {required && <span style={{ color: 'red' }}>*</span>}
    </Typography>
);

export const rowStyle = { 
    display: 'flex', 
    gap: 2, 
    flexDirection: { xs: 'column', md: 'row' },
    '& > *': { minWidth: 0 } // Đảm bảo các ô nhập tuân thủ tỷ lệ flex, không bị giãn theo chiều dài nội dung
};

export const TourItem = memo(({ item, index, isLocked, tourOptions, supplierOptions, onTourSelect, onChange, onRemove }) => (
    <Box sx={{ mb: 2, p: 1.5, border: '1px dashed #ccc', borderRadius: 2, bgcolor: '#fafafa' }}>
        <Box sx={{ ...rowStyle, alignItems: 'flex-start' }}>
            <Box flex={2}>
                <CustomLabel title="Tour chuẩn (Có sẵn)" />
                <Autocomplete
                    size="small" options={tourOptions || []} 
                    getOptionLabel={o => o.name ? `[${o.tour_code}] ${o.name}` : ''}
                    value={(tourOptions || []).find(t => t.tour_code === item.tour) || null}
                    disabled={isLocked} onChange={(e, v) => onTourSelect(index, v)}
                    renderInput={p => <TextField {...p} placeholder="Chọn tour..." />}
                />
            </Box>
            <Box flex={2}>
                <CustomLabel title="Tên Tour (Tùy chỉnh)" />
                <TextField size="small" fullWidth value={item.custom_tour_name || ''} disabled={isLocked} onChange={e => onChange('tours', index, 'custom_tour_name', e.target.value)} />
            </Box>
            <Box flex={2}>
                <CustomLabel title="Đại lý / NCC" />
                <Autocomplete 
                    size="small" options={supplierOptions} getOptionLabel={o => o.name || ''} 
                    value={item.supplier || null} disabled={isLocked} 
                    onChange={(e, v) => onChange('tours', index, 'supplier', v)} 
                    renderInput={p => <TextField {...p} />} 
                />
            </Box>
        </Box>
    <Box sx={{ ...rowStyle, alignItems: 'flex-end' }}>
            <Box flex={1.2}><CustomLabel title="Ngày đi" /><CustomDatePicker value={item.start_date || null} onChange={e => onChange('tours', index, 'start_date', e.target.value)} disabled={isLocked} /></Box>
            <Box flex={0.8}><CustomLabel title="Số ngày" /><TextField type="number" size="small" fullWidth value={item.duration_days || 1} disabled={isLocked} onChange={e => onChange('tours', index, 'duration_days', parseInt(e.target.value) || 1)} /></Box>
            <Box flex={0.8}><CustomLabel title="Người lớn" /><TextField type="number" size="small" fullWidth value={item.adults_count || 1} disabled={isLocked} onChange={e => onChange('tours', index, 'adults_count', parseInt(e.target.value) || 0)} /></Box>
            <Box flex={0.8}><CustomLabel title="Trẻ em" /><TextField type="number" size="small" fullWidth value={item.children_count || 0} disabled={isLocked} onChange={e => onChange('tours', index, 'children_count', parseInt(e.target.value) || 0)} /></Box>
            <Box flex={1.5}>
                <CustomLabel title="TT Điều hành" />
                <TextField select fullWidth size="small" value={item.operation_status || 'OPEN'} disabled={isLocked} onChange={(e) => onChange('tours', index, 'operation_status', e.target.value)}>
                    {COMMON_OPS_STATUS_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                </TextField>
            </Box>
            <Box sx={{ pb: 0.5 }}><IconButton onClick={() => onRemove('tours', index)} disabled={isLocked} color="error" size="small"><DeleteIcon /></IconButton></Box>
        </Box>
    </Box>
));

// --- 2. GUEST ITEM ---
export const GuestItem = memo(({ item, index, isLocked, onChange, onRemove, onViewPassport }) => {
    const hasPassportImage = !!item.passport_image;

    return (
        <Box sx={{ ...rowStyle, alignItems: 'flex-start' }}>
            <Box flex={2.5}><CustomLabel title={`Khách ${index + 1}`} /><TextField size="small" fullWidth value={item.name} disabled={isLocked} onChange={e => onChange('guests', index, 'name', e.target.value)} /></Box>
            <Box flex={1.5}><CustomLabel title="Loại" /><TextField select fullWidth size="small" value={item.guest_type} disabled={isLocked} onChange={e => onChange('guests', index, 'guest_type', e.target.value)}><MenuItem value="ADULT">Người lớn</MenuItem><MenuItem value="CHILD">Trẻ em</MenuItem></TextField></Box>
            <Box flex={1.5}><CustomLabel title="Ngày sinh" /><CustomDatePicker value={item.dob} onChange={e => onChange('guests', index, 'dob', e.target.value)} disabled={isLocked} /></Box>
            <Box flex={1.5}><CustomLabel title="Quốc tịch" /><TextField size="small" fullWidth value={item.nationality} disabled={isLocked} onChange={e => onChange('guests', index, 'nationality', e.target.value)} /></Box>
            <Box flex={1.5}><CustomLabel title="Số HC" /><TextField size="small" fullWidth value={item.passport} disabled={isLocked} onChange={e => onChange('guests', index, 'passport', e.target.value)} /></Box>
            {/* --- NÚT UPLOAD / XEM ẢNH PASSPORT --- */}
            <Box flex={1.5}>
                <CustomLabel title="Ảnh HC/CCCD" />
                {hasPassportImage ? (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            fullWidth
                            onClick={() => onViewPassport(item)}
                            startIcon={<VisibilityIcon />}
                            sx={{ 
                                height: '40px', 
                                textTransform: 'none', 
                                whiteSpace: 'nowrap',
                                borderColor: '#2e7d32', 
                                color: '#2e7d32',
                                fontWeight: 'bold',
                                bgcolor: 'rgba(46, 125, 50, 0.04)',
                                '&:hover': { borderColor: '#1b5e20' }
                            }}
                        >
                            Xem ảnh
                        </Button>
                        {!isLocked && (
                            <IconButton 
                                component="label" 
                                color="primary" 
                                size="small" 
                                sx={{ border: '1px dashed #ccc', borderRadius: 1, width: '40px', height: '40px' }}
                            >
                                <CloudUploadIcon fontSize="small" />
                                <input
                                    type="file"
                                    hidden
                                    accept="image/*,.pdf"
                                    onChange={(e) => {
                                        if (e.target.files && e.target.files[0]) {
                                            onChange('guests', index, 'passport_image', e.target.files[0]);
                                        }
                                    }}
                                />
                            </IconButton>
                        )}
                    </Box>
                ) : (
                    <Button
                        variant="outlined"
                        component="label"
                        size="small"
                        fullWidth
                        disabled={isLocked}
                        startIcon={<CloudUploadIcon />}
                        sx={{ 
                            height: '40px', 
                            textTransform: 'none', 
                            whiteSpace: 'nowrap',
                            borderColor: 'rgba(0, 0, 0, 0.23)', 
                            color: 'rgba(0, 0, 0, 0.6)',
                        }}
                    >
                        Tải ảnh
                        <input
                            type="file"
                            hidden
                            accept="image/*,.pdf"
                            onChange={(e) => {
                                if (e.target.files && e.target.files[0]) {
                                    onChange('guests', index, 'passport_image', e.target.files[0]);
                                }
                            }}
                        />
                    </Button>
                )}
            </Box>
            <Box sx={{ pt: 3 }}><IconButton onClick={() => onRemove('guests', index)} disabled={isLocked} color="error" size="small"><DeleteIcon /></IconButton></Box>
        </Box>
    );
});

// --- 3. PAYMENT ITEM ---
export const PaymentItem = memo(({ item, index, isLocked, supplierOptions, onChange, onRemove, onBillAction, onMoneyChange }) => {
    const isCollection = item.payment_type === 'COLLECTION';
    
    const isSettled = item.status === 'CONFIRMED' || item.status === 'SETTLED';
    const isRowLocked = isLocked || isSettled;

    const getStatusColor = (status) => {
        if (status === 'PENDING') return '#ed6c02';
        if (status === 'CONFIRMED' || status === 'SETTLED') return '#2e7d32';
        if (status === 'REJECTED') return '#d32f2f';
        return 'inherit';
    };

    const currentStatus = item.status || 'CONFIRMED';

    return (
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, alignItems: 'flex-start', mb: 1 }}>
            <Box flex={1.5}>
                <CustomLabel title="Hình thức" required />
                <TextField select fullWidth size="small" value={item.payment_type} 
                    disabled={isRowLocked}
                    onChange={e => onChange('payments', index, 'payment_type', e.target.value)}>
                    <MenuItem value="TRANSFER">Chuyển khoản</MenuItem>
                    <MenuItem value="CASH">Tiền mặt</MenuItem>
                    <MenuItem value="COLLECTION">Thu hộ (NCC)</MenuItem>
                </TextField>
            </Box>
            
            {isCollection && (
                <Box flex={2}>
                    <CustomLabel title="NCC Thu hộ" required />
                    <Autocomplete 
                        size="small" 
                        options={supplierOptions} 
                        getOptionLabel={o => o.name || ''} 
                        value={item.supplier} 
                        disabled={isRowLocked}
                        onChange={(e, v) => onChange('payments', index, 'supplier', v)} 
                        renderInput={p => <TextField {...p} placeholder="Chọn NCC" />} 
                    />
                </Box>
            )}
            
            <Box flex={1.5}>
                <CustomLabel title="Số tiền" required />
                <CurrencyInput 
                    value={{ 
                        original: item.amount_original ?? item.amount, 
                        currency: item.currency || 'VND', 
                        rate: item.rate || 1, 
                        vnd: item.amount || 0 
                    }} 
                    disabled={isRowLocked}
                    onChange={(obj) => onMoneyChange('payments', index, 'amount', obj)} 
                />
            </Box>
            
            <Box flex={2}>
                <CustomLabel title="Ghi chú" />
                <TextField size="small" fullWidth value={item.note || ''} 
                    disabled={isRowLocked}
                    onChange={e => onChange('payments', index, 'note', e.target.value)} placeholder="Nội dung..." />
            </Box>

            <Box flex={1.5}>
                <CustomLabel title="Trạng thái" />
                <TextField 
                    select fullWidth size="small" 
                    value={currentStatus} 
                    disabled={isLocked}
                    onChange={e => onChange('payments', index, 'status', e.target.value)}
                    sx={{
                        '& .MuiSelect-select': {
                            fontWeight: 'bold',
                            color: getStatusColor(currentStatus)
                        }
                    }}
                >
                    <MenuItem value="PENDING">Chờ duyệt</MenuItem>
                    <MenuItem value="CONFIRMED">Đã thực thu</MenuItem>
                    <MenuItem value="SETTLED">Đã quyết toán</MenuItem>
                    <MenuItem value="REJECTED">Từ chối</MenuItem>
                </TextField>
            </Box>
            
            <Box flex={1.2}>
                <CustomLabel title="Bill" />
                <BillButton item={item} onClick={onBillAction} />
            </Box>

            <Box sx={{ pt: 3.5 }}>
                <IconButton 
                    onClick={() => onRemove('payments', index)} 
                    disabled={isRowLocked}
                    color="error" size="small">
                    <DeleteIcon />
                </IconButton>
            </Box>
        </Box>
    );
});

// --- 4. ROOM ITEM ---
export const RoomItem = memo(({ item, index, isLocked, supplierOptions, roomOptions, toursList, onRoomSelect, onChange, onRemove }) => (
    <Box sx={{ mb: 2, p: 1.5, border: '1px dashed #ccc', borderRadius: 2, bgcolor: '#fafafa' }}>
        <Box sx={{ ...rowStyle, alignItems: 'flex-start' }}>
            <Box flex={2}><CustomLabel title="Mã/Hạng phòng chuẩn" /><Autocomplete size="small" options={roomOptions || []} getOptionLabel={o => o.room_name ? `[${o.room_code}] ${o.room_name}` : ''} value={(roomOptions || []).find(r => r.room_code === item.room_category) || null} disabled={isLocked} onChange={(e, v) => onRoomSelect(index, v)} renderInput={p => <TextField {...p} />} /></Box>
            <Box flex={2}><CustomLabel title="Hạng phòng (Tùy chỉnh)" /><TextField size="small" fullWidth value={item.room_type || ''} disabled={isLocked} onChange={e => onChange('rooms', index, 'room_type', e.target.value)} /></Box>
            <Box flex={2}><CustomLabel title="Khách sạn (NCC)" /><Autocomplete size="small" options={supplierOptions} getOptionLabel={o => o.name || ''} value={item.supplier} disabled={isLocked} onChange={(e, v) => onChange('rooms', index, 'supplier', v)} renderInput={p => <TextField {...p} />} /></Box>
        </Box>
        <Box sx={{ ...rowStyle, alignItems: 'flex-end' }}>
            <Box flex={1.2}><CustomLabel title="Check-in" /><CustomDatePicker value={item.check_in || null} onChange={e => onChange('rooms', index, 'check_in', e.target.value)} disabled={isLocked} /></Box>
            <Box flex={1.2}><CustomLabel title="Check-out" /><CustomDatePicker value={item.check_out || null} onChange={e => onChange('rooms', index, 'check_out', e.target.value)} disabled={isLocked} /></Box>
            <Box flex={0.8}><CustomLabel title="Số đêm" /><TextField type="number" size="small" fullWidth value={item.nights_count || 1} disabled={isLocked} onChange={e => onChange('rooms', index, 'nights_count', parseInt(e.target.value) || 1)} /></Box>
            <Box flex={0.8}><CustomLabel title="SL phòng" /><TextField type="number" size="small" fullWidth value={item.quantity || 1} disabled={isLocked} onChange={e => onChange('rooms', index, 'quantity', parseInt(e.target.value) || 1)} /></Box>
            <Box flex={1.5}>
                <CustomLabel title="Thanh toán" />
                <TextField select fullWidth size="small" value={item.payment_method || 'INCLUDED'} disabled={isLocked} onChange={(e) => onChange('rooms', index, 'payment_method', e.target.value)}>
                    <MenuItem value="INCLUDED">Theo Tour</MenuItem>
                    <MenuItem value="SEPARATE">Riêng biệt</MenuItem>
                </TextField>
            </Box>
            <Box flex={1.5}>
                <CustomLabel title="Thuộc Tour/Chặng" />
                <TextField 
                    select fullWidth size="small" 
                    value={item.booking_tour || ''} 
                    disabled={isLocked} 
                    onChange={(e) => onChange('rooms', index, 'booking_tour', e.target.value)}
                    SelectProps={{
                        sx: { '& .MuiSelect-select': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }
                    }}
                >
                    <MenuItem value="">-- Ngoài Tour --</MenuItem>
                    {(toursList || []).map((t, idx) => (
                        t.id ? <MenuItem key={t.id} value={t.id}>{t.custom_tour_name || t.tour || `Tour ${idx + 1}`}</MenuItem> : null
                    ))}
                </TextField>
            </Box>
            <Box flex={1.5}>
                <CustomLabel title="TT Điều hành" />
                <TextField select fullWidth size="small" value={item.operation_status || 'OPEN'} disabled={isLocked} onChange={(e) => onChange('rooms', index, 'operation_status', e.target.value)}>
                    {COMMON_OPS_STATUS_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                </TextField>
            </Box>
            <Box sx={{ pb: 0.5 }}><IconButton onClick={() => onRemove('rooms', index)} disabled={isLocked} color="error" size="small"><DeleteIcon /></IconButton></Box>
        </Box>
    </Box>
));

// --- 5. EXTRA ITEM ---
export const ExtraItem = memo(({ item, index, isLocked, supplierOptions, toursList, onChange, onMoneyChange, onRemove }) => {
    return (
        <Box sx={{ ...rowStyle, alignItems: 'flex-start' }}>
            <Box flex={2}><CustomLabel title="Tên DV" /><TextField size="small" fullWidth value={item.title} disabled={isLocked} onChange={e => onChange('extras', index, 'title', e.target.value)} /></Box>
            <Box flex={1}><CustomLabel title="SL" /><TextField type="number" size="small" fullWidth value={item.quantity} disabled={isLocked} onChange={e => onChange('extras', index, 'quantity', parseInt(e.target.value) || 0)} /></Box>
            <Box flex={2}><CustomLabel title="NCC" /><Autocomplete size="small" options={supplierOptions} getOptionLabel={o => o.name || ''} value={item.supplier} disabled={isLocked} onChange={(e, v) => onChange('extras', index, 'supplier', v)} renderInput={p => <TextField {...p} />} /></Box>
            <Box flex={1}><CustomLabel title="Ngày SD" /><CustomDatePicker value={item.service_date || null} onChange={e => onChange('extras', index, 'service_date', e.target.value)} disabled={isLocked} /></Box>
            <Box flex={1}>
                <CustomLabel title="Thanh toán" />
                <TextField select fullWidth size="small" value={item.payment_method || 'SEPARATE'} disabled={isLocked} onChange={(e) => onChange('extras', index, 'payment_method', e.target.value)}>
                    <MenuItem value="INCLUDED">Theo Tour</MenuItem>
                    <MenuItem value="SEPARATE">Riêng biệt</MenuItem>
                </TextField>
            </Box>
            <Box flex={1.5}>
                <CustomLabel title="Thuộc Tour/Chặng" />
                <TextField 
                    select fullWidth size="small" 
                    value={item.booking_tour || ''} 
                    disabled={isLocked} 
                    onChange={(e) => onChange('extras', index, 'booking_tour', e.target.value)}
                    SelectProps={{
                        sx: { '& .MuiSelect-select': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }
                    }}
                >
                    <MenuItem value="">-- Ngoài Tour --</MenuItem>
                    {(toursList || []).map((t, idx) => (
                        t.id ? <MenuItem key={t.id} value={t.id}>{t.custom_tour_name || t.tour || `Tour ${idx + 1}`}</MenuItem> : null
                    ))}
                </TextField>
            </Box>
            <Box flex={1.5}>
                <CustomLabel title="TT Điều hành" />
                <TextField select fullWidth size="small" value={item.operation_status || 'OPEN'} disabled={isLocked} onChange={(e) => onChange('extras', index, 'operation_status', e.target.value)}>
                    {COMMON_OPS_STATUS_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                </TextField>
            </Box>
            <Box sx={{ pt: 3 }}><IconButton onClick={() => onRemove('extras', index)} disabled={isLocked} color="error" size="small"><DeleteIcon /></IconButton></Box>
        </Box>
    );
});

export const TransportItem = memo(({ item, index, isLocked, supplierOptions, routeOptions, toursList, onRouteSelect, onChange, onMoneyChange, onRemove }) => {
    return (
        <Box sx={{ mb: 2, p: 2, border: '1px dashed #ccc', borderRadius: 2, bgcolor: '#fafafa' }}>
            {/* DÒNG 1: Thông tin cấu hình tuyến và nhà xe */}
            <Box sx={{ ...rowStyle, alignItems: 'flex-start' }}>
                <Box flex={2}>
                    <CustomLabel title="Tuyến xe" />
                    <Autocomplete 
                        size="small" 
                        options={routeOptions || []} 
                        getOptionLabel={o => {
                            if (o.route_name && o.route_code) return `[${o.route_code}] ${o.route_name}`;
                            if (o.route_name) return o.route_name;
                            return o.route_code || '';
                        }} 
                        // Thêm filterOptions để custom bộ lọc tìm kiếm
                        filterOptions={(options, { inputValue }) => {
                            const input = inputValue.toLowerCase();
                            return options.filter(o => {
                                const code = (o.route_code || '').toLowerCase();
                                const name = (o.route_name || '').toLowerCase();
                                const ncc = (o.supplier?.name || o.supplier_name || '').toLowerCase();
                                return code.includes(input) || name.includes(input) || ncc.includes(input);
                            });
                        }}
                        value={(routeOptions || []).find(r => r.route_code === item.route) || null} 
                        disabled={isLocked} 
                        onChange={(e, v) => onRouteSelect(index, v)} 
                        // Đã cập nhật lại placeholder cho rõ ràng hơn
                        renderInput={p => <TextField {...p} placeholder="Tìm mã, tên tuyến, NCC..." />} 
                    />
                </Box>
                <Box flex={2}>
                    <CustomLabel title="Nhà xe" />
                    <Autocomplete 
                        size="small" options={supplierOptions} getOptionLabel={o => o.name || ''} 
                        value={item.supplier} disabled={isLocked} 
                        onChange={(e, v) => onChange('transports', index, 'supplier', v)} 
                        renderInput={p => <TextField {...p} />} 
                    />
                </Box>
                <Box flex={1.5}>
                    <CustomLabel title="Loại xe" />
                    <TextField select size="small" fullWidth value={item.vehicle_type} disabled={isLocked} onChange={e => onChange('transports', index, 'vehicle_type', e.target.value)}>
                        <MenuItem value="SHUTTLE">Shuttle Bus</MenuItem>
                        <MenuItem value="LIMO">Limousine</MenuItem>
                        <MenuItem value="DCAR">Dcar</MenuItem>
                        <MenuItem value="SINGLE CABIN">Cabin Đơn</MenuItem>
                        <MenuItem value="COUPLE CABIN">Cabin Đôi</MenuItem>
                        <MenuItem value="SINGLE SLEEPER">Giường Nằm Đơn</MenuItem>
                        <MenuItem value="COUPLE SLEEPER">Giường Nằm Đôi</MenuItem>
                        <MenuItem value="4-SEAT TAXI">Taxi 4 Chỗ</MenuItem>
                        <MenuItem value="7-SEAT TAXI">Taxi 7 chỗ</MenuItem>
                    </TextField>
                </Box>
                <Box flex={1.5}>
                    <CustomLabel title="Chiều" />
                    <TextField select size="small" fullWidth value={item.direction} disabled={isLocked} onChange={e => onChange('transports', index, 'direction', e.target.value)}>
                        <MenuItem value="ONE_WAY_TO">Chiều đi</MenuItem><MenuItem value="ONE_WAY_FROM">Chiều về</MenuItem><MenuItem value="ROUND_TRIP">Hai chiều</MenuItem>
                    </TextField>
                </Box>
                <Box flex={1.5}>
                    <CustomLabel title="TT Điều hành" />
                    <TextField select fullWidth size="small" value={item.operation_status || 'OPEN'} disabled={isLocked} onChange={(e) => onChange('transports', index, 'operation_status', e.target.value)}>
                        {COMMON_OPS_STATUS_OPTIONS.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                    </TextField>
                </Box>
            </Box>

            {/* DÒNG 2: Thông tin chi tiết, điểm đón trả và tài chính */}
            <Box sx={{ ...rowStyle, alignItems: 'flex-start' }}>
                <Box flex={2}>
                    <CustomLabel title="TT Xe đón" />
                    <TextField size="small" fullWidth value={item.driver_info || ''} disabled={isLocked} onChange={e => onChange('transports', index, 'driver_info', e.target.value)} />
                </Box>
                <Box flex={2}>
                    <CustomLabel title="Điểm đón" />
                    <TextField size="small" fullWidth value={item.pickup_point || ''} disabled={isLocked} onChange={e => onChange('transports', index, 'pickup_point', e.target.value)} />
                </Box>
                <Box flex={2}>
                    <CustomLabel title="Điểm trả" />
                    <TextField size="small" fullWidth value={item.dropoff_point || ''} disabled={isLocked} onChange={e => onChange('transports', index, 'dropoff_point', e.target.value)} />
                </Box>
                <Box flex={1.5}>
                    <CustomLabel title="Giờ đón" />
                    <TextField type="datetime-local" size="small" fullWidth value={item.pickup_datetime || ''} disabled={isLocked} onChange={e => onChange('transports', index, 'pickup_datetime', e.target.value)} />
                </Box>
                <Box flex={1.5}>
                    <CustomLabel title="Thanh toán" />
                    <TextField select fullWidth size="small" value={item.payment_method || 'SEPARATE'} disabled={isLocked} onChange={(e) => onChange('transports', index, 'payment_method', e.target.value)}>
                        <MenuItem value="INCLUDED">Theo Tour</MenuItem>
                        <MenuItem value="SEPARATE">Riêng biệt</MenuItem>
                    </TextField>
                </Box>
                <Box flex={1.5}>
                    <CustomLabel title="Thuộc Tour" />
                    <TextField 
                        select fullWidth size="small" 
                        value={item.booking_tour || ''} 
                        disabled={isLocked} 
                        onChange={(e) => onChange('transports', index, 'booking_tour', e.target.value)}
                        SelectProps={{
                            sx: { '& .MuiSelect-select': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }
                        }}
                    >
                        <MenuItem value="">-- Ngoài Tour --</MenuItem>
                        {(toursList || []).map((t, idx) => (
                            t.id ? <MenuItem key={t.id} value={t.id}>{t.custom_tour_name || t.tour || `Tour ${idx + 1}`}</MenuItem> : null
                        ))}
                    </TextField>
                </Box>
                <Box sx={{ pt: 3 }}>
                    <IconButton onClick={() => onRemove('transports', index)} disabled={isLocked} color="error" size="small"><DeleteIcon /></IconButton>
                </Box>
            </Box>
        </Box>
    );
});

export const CustomerChargeItem = memo(({ item, index, isLocked, onChange, onMoneyChange, onRemove }) => (
    <Box sx={{ ...rowStyle, alignItems: 'flex-end' }}>
        <Box flex={3}><CustomLabel title="Tên Dịch vụ / Khoản thu" /><TextField size="small" fullWidth value={item.title || ''} disabled={isLocked} onChange={e => onChange('customer_charges', index, 'title', e.target.value)} /></Box>
        <Box flex={1}><CustomLabel title="Số lượng" /><TextField type="number" size="small" fullWidth value={item.quantity || 1} disabled={isLocked} onChange={e => onChange('customer_charges', index, 'quantity', parseFloat(e.target.value) || 0)} /></Box>
        <Box flex={2}>
            <CustomLabel title="Đơn giá" />
            <CurrencyInput 
                value={{ 
                    // Bổ sung truyền đủ 4 trường
                    original: item.unit_original ?? item.unit_price ?? 0, 
                    currency: item.unit_currency || 'VND', 
                    rate: item.unit_rate || 1, 
                    vnd: item.unit_price || 0 
                }} 
                disabled={isLocked} 
                onChange={obj => onMoneyChange('customer_charges', index, 'unit', obj)} 
            />
        </Box>
        <Box flex={2}><CustomLabel title="Thành tiền" /><TextField size="small" fullWidth disabled value={new Intl.NumberFormat('vi-VN').format((item.quantity || 1) * (item.unit_price || 0))} sx={{ '& input': { fontWeight: 'bold', color: '#007373' } }} /></Box>
        <Box sx={{ pb: 0.5 }}><IconButton onClick={() => onRemove('customer_charges', index)} disabled={isLocked} color="error" size="small"><DeleteIcon /></IconButton></Box>
    </Box>
));

export const LiabilityItem = memo(({ item, index, isLocked, supplierOptions, onChange, onMoneyChange, onRemove, onBillAction }) => {
    return (
        <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                
                {/* 1. Loại công nợ (Dropdown) */}
                <Box flex={1.5}>
                    <CustomLabel title="Loại công nợ" />
                    <TextField 
                        select 
                        fullWidth 
                        size="small" 
                        value={item.payable_type || 'OTHER'} 
                        disabled={isLocked} 
                        onChange={e => onChange('payables', index, 'payable_type', e.target.value)}
                    >
                        <MenuItem value="TOUR">CN Tour</MenuItem>
                        <MenuItem value="TRANSPORT_TO">CN Xe đi</MenuItem>
                        <MenuItem value="TRANSPORT_FROM">CN Xe về</MenuItem>
                        <MenuItem value="TRANSPORT_ROUND">CN Xe khứ hồi</MenuItem>
                        <MenuItem value="ROOM">CN Phòng</MenuItem>
                        <MenuItem value="EXTRA">CN DV khác</MenuItem>
                        <MenuItem value="OTHER">CN Khác</MenuItem>
                    </TextField>
                </Box>

                {/* 2. Đại lý / NCC */}
                <Box flex={2}>
                    <CustomLabel title="Đại lý / NCC" />
                    <Autocomplete 
                        size="small" 
                        options={supplierOptions} 
                        getOptionLabel={o => o.name || ''} 
                        value={item.supplier || null} 
                        disabled={isLocked} 
                        onChange={(e, v) => onChange('payables', index, 'supplier', v)} 
                        renderInput={p => <TextField {...p} />} 
                    />
                </Box>

                {/* 3. Ghi chú / Tên DV */}
                <Box flex={3}>
                    <CustomLabel title="Ghi chú / Tên Dịch vụ" />
                    <TextField 
                        size="small" 
                        fullWidth 
                        value={item.note || ''} 
                        disabled={isLocked} 
                        onChange={e => onChange('payables', index, 'note', e.target.value)} 
                    />
                </Box>

                {/* 4. Thành tiền */}
                <Box flex={2}>
                    <CustomLabel title="Thành tiền" />
                    <CurrencyInput 
                        value={{ 
                            original: item.amount_original || 0, 
                            currency: item.currency || 'VND', 
                            rate: item.exchange_rate || 1, 
                            vnd: item.amount || 0 
                        }} 
                        disabled={isLocked} 
                        onChange={obj => onMoneyChange('payables', index, 'amount', obj)} 
                    />
                </Box>

                {/* 5. Trạng thái TT */}
                <Box flex={1.5}>
                    <CustomLabel title="Trạng thái TT" />
                    <TextField 
                        select 
                        fullWidth 
                        size="small" 
                        value={item.status || 'UNPAID'} 
                        disabled={isLocked} 
                        onChange={e => onChange('payables', index, 'status', e.target.value)}
                    >
                        <MenuItem value="UNPAID">Chưa TT</MenuItem>
                        <MenuItem value="APPROVED">Đã duyệt</MenuItem>
                        <MenuItem value="PAID">Đã TT</MenuItem>
                    </TextField>
                </Box>

                {/* 6. Bill (Icon Button Rút Gọn) */}
                <Box flex={0.8} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <CustomLabel title="Bill" />
                    <IconButton 
                        onClick={onBillAction}
                        size="small"
                        sx={{ 
                            border: '1px solid', 
                            borderColor: item.bill_image || item.image || item.bill_url ? '#2e7d32' : 'rgba(0,0,0,0.23)', 
                            color: item.bill_image || item.image || item.bill_url ? '#2e7d32' : 'rgba(0,0,0,0.6)',
                            borderRadius: 1,
                            width: '40px',
                            height: '40px'
                        }}
                    >
                        {item.bill_image || item.image || item.bill_url ? <VisibilityIcon /> : <CloudUploadIcon />}
                    </IconButton>
                </Box>

                {/* 7. Hành động Xóa */}
                <Box sx={{ pt: 4 }}>
                    <IconButton onClick={() => onRemove('payables', index)} disabled={isLocked} color="error" size="small">
                        <DeleteIcon />
                    </IconButton>
                </Box>
            </Box>
        </Box>
    );
});